import React from 'react';
import { Link } from 'react-router-dom';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';

const WINDOW_CLEANING_FAQ = [
  {
    question: 'How often should commercial windows be cleaned?',
    answer: 'For most businesses, we recommend professional window cleaning at least quarterly. High-traffic or storefront locations may benefit from more frequent service to maintain a spotless appearance.'
  },
  {
    question: 'Is your window cleaning process safe for tinted or specialty glass?',
    answer: {
      intro: 'Yes, we use gentle, non-abrasive methods and pure water technology that are safe for:',
      list: [
        'Tinted windows',
        'Tempered glass',
        'Low-E and insulated glass',
        'Storefront and display windows'
      ],
      outro: 'Our technicians are trained to identify and care for all types of commercial glass.'
    }
  },
  {
    question: 'Can you clean windows on multi-story buildings?',
    answer: 'Absolutely! We are equipped to safely clean windows on buildings of all sizes, including multi-story offices, retail centers, and more.'
  }
];

const WindowCleaning = () => {
  const serviceSEO = {
    title: 'Window Cleaning Services | Copper Canyon Pressure Wash',
    description: 'Professional window cleaning in Phoenix, AZ. Streak-free results for all glass types. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Window Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Window Cleaning'
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Window Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional window cleaning in Phoenix, AZ. Remove streaks & hard water spots. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="residential window cleaning, commercial window cleaning, Phoenix window maintenance, storefront cleaning, high-rise windows, window screen cleaning, window streak removal, window restoration, window surface cleaning, window safety cleaning"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Window Cleaning Banner">
        <picture>
          <source srcSet="/window_washing_hero.avif" type="image/avif" />
          <source srcSet="/window_washing_hero.webp" type="image/webp" />
        <img 
          src="/window_washing_hero.jpeg" 
          alt="Professional window cleaning in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Window Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Window Cleaning Services</h2>
        <p className="mb-6 text-gray-700">We provide expert window cleaning for commercial properties, offices, and retail spaces. Our team uses advanced equipment and eco-friendly solutions to deliver streak-free, sparkling results every time.</p>
        <h3 className="text-xl font-semibold text-[#4a3f35] mb-2">Why Choose Us?</h3>
        <ul className="list-disc pl-6 mb-6 text-gray-700">
          <li>Experienced, professional technicians</li>
          <li>Pure water and eco-friendly cleaning solutions</li>
          <li>Safe for all types of commercial glass</li>
          <li>Flexible scheduling for businesses</li>
          <li>100% satisfaction guarantee</li>
        </ul>
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Window Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our window cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ items={WINDOW_CLEANING_FAQ} title="Frequently Asked Questions About Window Cleaning" />
    </div>
  );
};

export default WindowCleaning; 